import React, { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, ExternalLink, Check, X, AlertCircle, Filter } from 'lucide-react';
import { WorkshopManager } from '../../helpers/workshop';
import { showToast } from '../../helpers/customToast';

const WorkshopReservationCard = ({ reservation }) => {
  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending_payment':
        return <span className="badge bg-warning">En attente de paiement</span>;
      case 'confirmed':
        return <span className="badge bg-success">Confirmé</span>;
      case 'cancelled':
        return <span className="badge bg-danger">Annulé</span>;
      case 'completed':
        return <span className="badge bg-info">Terminé</span>;
      default:
        return <span className="badge bg-secondary">En attente</span>;
    }
  };

  const workshopDate = Array.isArray(reservation.workshop_date)
    ? reservation.workshop_date[0]
    : reservation.workshop_date;

  if (!workshopDate || !workshopDate.date) {
    console.error('Invalid workshop_date in reservation:', reservation);
    return null; // Skip rendering this card if date is missing
  }

  const isFutureWorkshop = () => {
    return new Date(workshopDate.date) > new Date();
  };

  const isPastWorkshop = () => {
    return new Date(workshopDate.date) < new Date();
  };

  return (
    <div className="card mb-3">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div>
            <h5 className="card-title mb-1">{reservation.workshop?.title || 'Atelier'}</h5>
            <div className="text-muted">Réservation #{reservation.id}</div>
          </div>
          {getStatusBadge(reservation.resa_status || reservation.status)}
        </div>

        <div className="d-flex flex-wrap mb-3">
          <div className="me-4 mb-2">
            <div className="d-flex align-items-center text-muted">
              <Calendar size={16} className="me-2" />
              <span>{formatDate(workshopDate.date)}</span>
            </div>
          </div>
          
          <div className="me-4 mb-2">
            <div className="d-flex align-items-center text-muted">
              <Clock size={16} className="me-2" />
              <span>Durée: {reservation.workshop.duration} min</span>
            </div>
          </div>
          
          {reservation.workshop.location && (
            <div className="mb-2">
              <div className="d-flex align-items-center text-muted">
                <MapPin size={16} className="me-2" />
                <span>{reservation.workshop.location}</span>
              </div>
            </div>
          )}
        </div>

        <div className="d-flex justify-content-between align-items-center">
          <div>
            <strong>Prix:</strong> {reservation.total_price.toFixed(2)}€
          </div>
          
          {reservation.status === 'confirmed' && isFutureWorkshop() && (
            <a 
              href={`/atelier-confirmation?document_id=${reservation.documentId}`} 
              className="btn btn-outline-primary btn-sm"
            >
              <ExternalLink size={16} className="me-1" />
              Voir les détails
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

const UserWorkshopsComponent = ({ userData }) => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, upcoming, past

  useEffect(() => {
    const fetchReservations = async () => {
      try {
        setLoading(true);
        const reservationsData = await WorkshopManager.getUserReservations(userData.id);
        setReservations(reservationsData);
      } catch (error) {
        console.error('Error fetching user reservations:', error);
        showToast('Erreur lors du chargement des réservations', 'error');
      } finally {
        setLoading(false);
      }
    };

    if (userData.id) {
      fetchReservations();
    }
  }, [userData.id]);

  const filteredReservations = () => {
    if (filter === 'all') return reservations;

    const now = new Date();

    if (filter === 'upcoming') {
      return reservations.filter(res => {
        const workshopDate = Array.isArray(res.workshop_date) ? res.workshop_date[0] : res.workshop_date;
        if (!workshopDate || !workshopDate.date) return false;
        return new Date(workshopDate.date) > now && res.resa_status !== 'cancelled';
      });
    }

    if (filter === 'past') {
      return reservations.filter(res => {
        const workshopDate = Array.isArray(res.workshop_date) ? res.workshop_date[0] : res.workshop_date;
        if (!workshopDate || !workshopDate.date) return false;
        return new Date(workshopDate.date) < now || res.resa_status === 'cancelled';
      });
    }

    return reservations;
  };

  const rendered = filteredReservations();

  return (
    <div className="user-workshops">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Vos réservations d'ateliers</h3>
        
        <div className="btn-group">
          <button 
            className={`btn btn-sm ${filter === 'all' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilter('all')}
          >
            Toutes
          </button>
          <button 
            className={`btn btn-sm ${filter === 'upcoming' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilter('upcoming')}
          >
            À venir
          </button>
          <button 
            className={`btn btn-sm ${filter === 'past' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setFilter('past')}
          >
            Passées
          </button>
        </div>
      </div>
      
      {loading ? (
        <div className="text-center py-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
          <p className="mt-2">Chargement de vos réservations...</p>
        </div>
      ) : rendered.length === 0 ? (
        <div className="alert alert-info">
          <div className="d-flex align-items-center">
            <AlertCircle size={18} className="me-2" />
            <div>
              <p className="mb-0">
                {filter === 'all' 
                  ? "Vous n'avez pas encore réservé d'atelier." 
                  : filter === 'upcoming' 
                    ? "Vous n'avez pas d'ateliers à venir." 
                    : "Vous n'avez pas d'ateliers passés."}
              </p>
              {filter !== 'all' && (
                <button 
                  className="btn btn-link p-0 text-info" 
                  onClick={() => setFilter('all')}
                >
                  Voir toutes les réservations
                </button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-3">
            <small className="text-muted">
              {rendered.length} réservation{rendered.length > 1 ? 's' : ''} trouvée{rendered.length > 1 ? 's' : ''}
            </small>
          </div>
          {rendered.map(reservation => (
            <WorkshopReservationCard 
              key={reservation.id} 
              reservation={reservation} 
            />
          ))}
        </>
      )}
      
      <div className="text-center mt-4">
        <a href="/ateliers" className="btn btn-primary">
          <Calendar size={18} className="me-2" />
          Découvrir nos ateliers
        </a>
      </div>
    </div>
  );
};

export default UserWorkshopsComponent;